import type { SpecialEvent } from "domains/specialEvents/pages/main"
import { normalizeSpecialEvent } from "domains/specialEvents/normalization"
import { getGameLabel, getTargetingWarnings, looksLikeTargetingKey } from "domains/specialEvents/targeting"

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
)

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ZONED_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/i

const hasValidCalendarDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return false
  const [, year, month, day] = match.map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))

  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
}

const normalizeImportDate = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined
  const normalized = DATE_ONLY_PATTERN.test(value) ? `${value}T00:00:00.000Z` : value
  if (!ZONED_DATE_TIME_PATTERN.test(normalized)
    || !hasValidCalendarDate(normalized)
    || Number.isNaN(Date.parse(normalized))) return undefined

  return new Date(normalized).toISOString()
}

const isStringArray = (value: unknown): value is string[] => (
  Array.isArray(value) && value.every(item => typeof item === "string")
)

const hasInvalidOptionalType = (
  value: Record<string, unknown>,
  keys: string[],
  expectedType: "boolean" | "number" | "string",
) => keys.some(key => value[key] != null && typeof value[key] !== expectedType)

const hasNegativeNumber = (value: Record<string, unknown>, keys: string[]) => keys.some(
  key => typeof value[key] === "number" && (value[key] as number) < 0,
)

const isPixelArray = (value: unknown) => Array.isArray(value) && value.every(pixel => (
  typeof pixel === "string" || (isRecord(pixel) && typeof pixel.src === "string")
))

const validateBanner = (banner: unknown, path: string, errors: string[]) => {
  if (banner == null) return
  if (!isRecord(banner)) {
    errors.push(`${path} must be an object`)

    return
  }
  if (typeof banner.image !== "string" || typeof banner.cta !== "string") {
    errors.push(`${path}.image and ${path}.cta must be strings`)
  }
  if (banner.trackingPixels != null && !isPixelArray(banner.trackingPixels)) {
    errors.push(`${path}.trackingPixels must be an array of URLs or pixel objects`)
  }
}

const validateNotification = (notification: unknown, path: string, errors: string[]) => {
  if (!isRecord(notification)) {
    errors.push(`${path} must be an object`)

    return
  }

  if (notification.uid != null && (typeof notification.uid !== "string" || !notification.uid.trim())) {
    errors.push(`${path}.uid must be a non-empty string when provided`)
  }
  if (typeof notification.triggerEventName !== "string" || !notification.triggerEventName.trim()) {
    errors.push(`${path}.triggerEventName must be a string`)
  }
  if (hasInvalidOptionalType(notification, ["enabled", "ignoreSpawnCap", "playSound"], "boolean")) {
    errors.push(`${path} has an invalid boolean setting`)
  }
  if (hasInvalidOptionalType(notification, [
    "spawnProbability",
    "buffsReward",
    "duration",
    "triggerDelay",
    "cooldown",
    "animationDuration",
    "contestPointsReward",
  ], "number")) errors.push(`${path} has an invalid numeric setting`)
  if (hasNegativeNumber(notification, [
    "buffsReward",
    "duration",
    "triggerDelay",
    "cooldown",
    "animationDuration",
    "contestPointsReward",
  ])) errors.push(`${path} has a negative numeric setting`)
  if (typeof notification.spawnProbability === "number"
    && (notification.spawnProbability < 0 || notification.spawnProbability > 1)) {
    errors.push(`${path}.spawnProbability must be between 0 and 1`)
  }
  if (notification.ingameOverlayType != null
    && notification.ingameOverlayType !== "stacking"
    && notification.ingameOverlayType !== "generic") {
    errors.push(`${path}.ingameOverlayType must be stacking or generic`)
  }
  if (notification.excludedIsps != null && !isStringArray(notification.excludedIsps)) {
    errors.push(`${path}.excludedIsps must be an array of strings`)
  }
  const pixelFields = ["trackingPixels", "clickTrackingPixels"]
  pixelFields.forEach(key => {
    if (notification[key] != null && !isPixelArray(notification[key])) {
      errors.push(`${path}.${key} must be an array of URLs or pixel objects`)
    }
  })
  if (notification.sourceImagesByGeo != null && !Array.isArray(notification.sourceImagesByGeo)) {
    errors.push(`${path}.sourceImagesByGeo must be an array`)
  }
}

const validateGameConfig = (gameConfig: unknown, index: number, errors: string[]) => {
  const path = `notificationsPerGame[${index}]`
  if (!isRecord(gameConfig)) {
    errors.push(`${path} must be an object`)

    return
  }
  if ((typeof gameConfig.gameId !== "string" && typeof gameConfig.gameId !== "number")
    || !String(gameConfig.gameId).trim()) errors.push(`${path}.gameId is required`)
  if (hasInvalidOptionalType(gameConfig, ["notificationsSpawnCap", "stackingWrapperTopOffset"], "number")) {
    errors.push(`${path} has an invalid numeric setting`)
  }
  if (hasNegativeNumber(gameConfig, ["notificationsSpawnCap"])) errors.push(`${path}.notificationsSpawnCap cannot be negative`)
  if (gameConfig.stackingWrapperTopOffsetUnit !== undefined
    && gameConfig.stackingWrapperTopOffsetUnit !== "%"
    && gameConfig.stackingWrapperTopOffsetUnit !== "px") {
    errors.push(`${path}.stackingWrapperTopOffsetUnit must be % or px`)
  }
  if (gameConfig.notifications !== undefined && !Array.isArray(gameConfig.notifications)) {
    errors.push(`${path}.notifications must be an array`)

    return
  }
  const notifications = gameConfig.notifications as unknown[] | undefined
  notifications?.forEach((notification, notificationIndex) => {
    validateNotification(notification, `${path}.notifications[${notificationIndex}]`, errors)
  })
  const notificationUids = (notifications || [])
    .filter(isRecord)
    .map(notification => notification.uid)
    .filter((uid): uid is string => typeof uid === "string" && Boolean(uid.trim()))
  if (notificationUids.length !== new Set(notificationUids).size) {
    errors.push(`${path}.notifications must have unique uid values`)
  }
  validateBanner(gameConfig.launchGameBanner, `${path}.launchGameBanner`, errors)
  validateBanner(gameConfig.endgameBanner, `${path}.endgameBanner`, errors)
  // A misspelled container key would import clean and serve everywhere, which a misspelled rule key
  // does not, so the two have to be caught in the same place.
  const misspelledKey = Object.keys(gameConfig).find(looksLikeTargetingKey)
  if (misspelledKey) errors.push(`${path}.${misspelledKey} should be "targeting"`)
  // targeting itself is checked by getImportTargetingWarnings, not here: refusing the import would
  // close export -> fix -> import for exactly the campaign that needs repairing.
}

// Warnings for a campaign that has already parsed, so the import modal can report the same things
// the game card would without blocking the paste.
export const getImportTargetingWarnings = (event: SpecialEvent): string[] => (
  (event.notificationsPerGame || []).flatMap(gameConfig => {
    const label = getGameLabel(gameConfig?.gameId)

    return getTargetingWarnings(gameConfig?.targeting, gameConfig?.gameId)
      .map(warning => `${label}: ${warning}`)
  })
)

const getCampaignCandidate = (value: unknown): Record<string, unknown> => {
  if (!isRecord(value)) {
    throw new Error("The JSON must contain a campaign object or a specialEvents array.")
  }

  if (!("specialEvents" in value)) return value

  if (!Array.isArray(value.specialEvents)) {
    throw new Error("specialEvents must be an array.")
  }

  if (value.specialEvents.length !== 1) {
    throw new Error("Import one campaign at a time. The specialEvents array must contain exactly one campaign.")
  }

  const [campaign] = value.specialEvents
  if (!isRecord(campaign)) {
    throw new Error("The campaign inside specialEvents must be a JSON object.")
  }

  return campaign
}

export const parseSpecialEventJson = (json: string): SpecialEvent => {
  let parsed: unknown

  try {
    parsed = JSON.parse(json)
  } catch (error) {
    const detail = error instanceof Error ? ` ${error.message}` : ""

    throw new Error(`This is not valid JSON. Check for missing commas, quotes, or brackets.${detail}`)
  }

  const campaign = getCampaignCandidate(parsed)
  const errors: string[] = []

  if (typeof campaign.name !== "string" || !campaign.name.trim()) errors.push("name")
  if (!Array.isArray(campaign.games) || campaign.games.some(gameId => (
    (typeof gameId !== "string" && typeof gameId !== "number") || !String(gameId).trim()
  ))) errors.push("games")
  const startDate = normalizeImportDate(campaign.startDate)
  const endDate = normalizeImportDate(campaign.endDate)
  if (!startDate) errors.push("startDate")
  if (!endDate) errors.push("endDate")
  if (!Array.isArray(campaign.notificationsPerGame)) errors.push("notificationsPerGame")
  const stringArrayFields = ["restrictedToCountries", "restrictedToRegions", "excludedIsps"]
  stringArrayFields.forEach(key => {
    if (campaign[key] != null && !isStringArray(campaign[key])) errors.push(key)
  })
  if (hasInvalidOptionalType(campaign, ["weight", "abSplit"], "number")) errors.push("numeric campaign settings")
  if (hasNegativeNumber(campaign, ["weight"])) errors.push("weight")
  if (campaign.abSplit !== undefined
    && typeof campaign.abSplit === "number"
    && (campaign.abSplit < 0 || campaign.abSplit > 1)) errors.push("abSplit")

  if (errors.length > 0) {
    throw new Error(`The campaign is missing or has an invalid value for: ${errors.join(", ")}.`)
  }

  if (Date.parse(endDate as string) <= Date.parse(startDate as string)) {
    throw new Error("endDate must be later than startDate.")
  }

  const games = (campaign.games as unknown[]).map(gameId => String(gameId))
  const configErrors: string[] = []
  const gameConfigs = campaign.notificationsPerGame as unknown[]
  gameConfigs.forEach((gameConfig, index) => validateGameConfig(gameConfig, index, configErrors))
  if (configErrors.length > 0) throw new Error(`Invalid campaign values: ${configErrors.join("; ")}.`)

  const configuredGames = (campaign.notificationsPerGame as Record<string, unknown>[]).map(gameConfig => String(gameConfig.gameId))
  const uniqueGames = new Set(games)
  const uniqueConfiguredGames = new Set(configuredGames)
  if (games.length !== uniqueGames.size
    || configuredGames.length !== uniqueConfiguredGames.size
    || uniqueGames.size !== uniqueConfiguredGames.size
    || games.some(gameId => !uniqueConfiguredGames.has(gameId))
    || configuredGames.some(gameId => !uniqueGames.has(gameId))) {
    throw new Error("games and notificationsPerGame must contain the same game ids.")
  }

  const portableCampaign: SpecialEvent = {
    uid: typeof campaign.uid === "string" ? campaign.uid : "",
    name: campaign.name as string,
    games,
    startDate: startDate as string,
    endDate: endDate as string,
    notificationsPerGame: campaign.notificationsPerGame as SpecialEvent["notificationsPerGame"],
  }
  if (isStringArray(campaign.restrictedToCountries)) portableCampaign.restrictedToCountries = campaign.restrictedToCountries
  if (isStringArray(campaign.restrictedToRegions)) portableCampaign.restrictedToRegions = campaign.restrictedToRegions
  if (isStringArray(campaign.excludedIsps)) portableCampaign.excludedIsps = campaign.excludedIsps
  if (typeof campaign.weight === "number") portableCampaign.weight = campaign.weight
  if (typeof campaign.defaultSoundFile === "string") portableCampaign.defaultSoundFile = campaign.defaultSoundFile
  if (typeof campaign.notificationHotkey === "string") portableCampaign.notificationHotkey = campaign.notificationHotkey
  if (typeof campaign.abSplit === "number") portableCampaign.abSplit = campaign.abSplit

  return normalizeSpecialEvent(portableCampaign)
}

const getSafeFileName = (name: string) => name
  .trim()
  .toLowerCase()
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")

export const createSpecialEventExport = (event: SpecialEvent) => {
  const campaign: SpecialEvent = {
    uid: event.uid,
    name: event.name,
    games: event.games,
    startDate: event.startDate,
    endDate: event.endDate,
    notificationsPerGame: event.notificationsPerGame,
  }
  if (event.restrictedToCountries !== undefined) campaign.restrictedToCountries = event.restrictedToCountries
  if (event.restrictedToRegions !== undefined) campaign.restrictedToRegions = event.restrictedToRegions
  if (event.excludedIsps !== undefined) campaign.excludedIsps = event.excludedIsps
  if (event.weight !== undefined) campaign.weight = event.weight
  if (event.defaultSoundFile !== undefined) campaign.defaultSoundFile = event.defaultSoundFile
  if (event.notificationHotkey !== undefined) campaign.notificationHotkey = event.notificationHotkey
  if (event.abSplit !== undefined) campaign.abSplit = event.abSplit

  return {
    fileName: `${getSafeFileName(event.name) || "special-event"}.special-event.json`,
    json: JSON.stringify({ specialEvents: [campaign] }, null, 2),
  }
}
